/**
 * Queries and mutations for "Who should this go to?", the reassign and the
 * routing register (16 Oct). Suggestions are asked for, not cached: every ask
 * is a fresh read of the ticket.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actOnRoutingRule as sdkActOnRule,
  addRoutingDispatcher as sdkAddDispatcher,
  addRoutingRule as sdkAddRule,
  type AssignSuggestionResponse,
  type AssignTeamListResponse,
  type AssignTeamMembersResponse,
  type AutoRouteRequest,
  type AutoRouteResponse,
  decideRouting as sdkDecide,
  getRoutingRegister as sdkGetRegister,
  learnRoutingOutcomes as sdkLearn,
  listAssignTeamMembers as sdkListMembers,
  listAssignTeams as sdkListTeams,
  listRoutingAudit as sdkListAudit,
  type ReassignRequest,
  type ReassignResponse,
  reassignTicket as sdkReassign,
  removeDispatcher as sdkRemoveDispatcher,
  type RoutingAuditResponse,
  type RoutingAwayInput,
  type RoutingDecisionRequest,
  type RoutingDecisionResponse,
  type RoutingDispatcher,
  type RoutingDispatcherInput,
  type RoutingRegisterResponse,
  type RoutingRule,
  type RoutingRuleAction,
  type RoutingRuleInput,
  type RoutingSettingsInfo,
  runAutoRoute as sdkRunAutoRoute,
  setDispatcherAway as sdkSetAway,
  suggestAssignment as sdkSuggest,
  updateRoutingRule as sdkUpdateRule,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export const assignKeys = {
  all: ["assign"] as const,
  teams: () => [...assignKeys.all, "teams"] as const,
  members: (teamId: string) => [...assignKeys.all, "members", teamId] as const,
  register: () => [...assignKeys.all, "register"] as const,
  audit: () => [...assignKeys.all, "audit"] as const,
};

/** Ask: by ticket number, or by pasted text. */
export function useSuggestAssignment() {
  return useMutation({
    mutationFn: async (body: {
      ticket_number?: string;
      text?: string;
    }): Promise<AssignSuggestionResponse> => {
      logger.debug("[assign] suggest", body.ticket_number ?? "text");
      const { data } = await sdkSuggest({ body, throwOnError: true });
      return data;
    },
  });
}

export function useAssignTeams(enabled = true) {
  return useQuery({
    queryKey: assignKeys.teams(),
    queryFn: async (): Promise<AssignTeamListResponse> => {
      const { data } = await sdkListTeams({ throwOnError: true });
      return data;
    },
    enabled,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useAssignTeamMembers(teamId: string | null) {
  return useQuery({
    queryKey: assignKeys.members(teamId ?? ""),
    queryFn: async (): Promise<AssignTeamMembersResponse> => {
      const { data } = await sdkListMembers({
        path: { team_id: teamId ?? "" },
        throwOnError: true,
      });
      return data;
    },
    enabled: teamId !== null && teamId !== "",
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useReassign(ticketId: string) {
  return useMutation({
    mutationFn: async (body: ReassignRequest): Promise<ReassignResponse> => {
      logger.debug("[assign] reassign", ticketId, body.reason);
      const { data } = await sdkReassign({
        path: { ticket_id: ticketId },
        body,
        throwOnError: true,
      });
      return data;
    },
  });
}

export function useRoutingDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RoutingDecisionRequest): Promise<RoutingDecisionResponse> => {
      const { data } = await sdkDecide({ body, throwOnError: true });
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: assignKeys.register() }),
  });
}

export function useRoutingRegister() {
  return useQuery({
    queryKey: assignKeys.register(),
    queryFn: async (): Promise<RoutingRegisterResponse> => {
      const { data } = await sdkGetRegister({ throwOnError: true });
      return data;
    },
    retry: false,
  });
}

export function useRoutingAudit(enabled: boolean) {
  return useQuery({
    queryKey: assignKeys.audit(),
    queryFn: async (): Promise<RoutingAuditResponse> => {
      const { data } = await sdkListAudit({ throwOnError: true });
      return data;
    },
    enabled,
    retry: false,
  });
}

function useRefreshRegister() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: assignKeys.register() });
    void queryClient.invalidateQueries({ queryKey: assignKeys.audit() });
  };
}

export function useSaveRule() {
  const refresh = useRefreshRegister();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number | null;
      body: RoutingRuleInput;
    }): Promise<RoutingRule> => {
      if (id === null) {
        const { data } = await sdkAddRule({ body, throwOnError: true });
        return data;
      }
      const { data } = await sdkUpdateRule({ path: { rule_id: id }, body, throwOnError: true });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useRuleAction() {
  const refresh = useRefreshRegister();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: RoutingRuleAction;
    }): Promise<RoutingRule> => {
      const { data } = await sdkActOnRule({ path: { rule_id: id }, body, throwOnError: true });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useAddDispatcher() {
  const refresh = useRefreshRegister();
  return useMutation({
    mutationFn: async (body: RoutingDispatcherInput): Promise<RoutingDispatcher> => {
      const { data } = await sdkAddDispatcher({ body, throwOnError: true });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useSetAway() {
  const refresh = useRefreshRegister();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: RoutingAwayInput;
    }): Promise<RoutingDispatcher> => {
      const { data } = await sdkSetAway({
        path: { dispatcher_id: id },
        body,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useRemoveDispatcher() {
  const refresh = useRefreshRegister();
  return useMutation({
    mutationFn: async (id: number): Promise<RoutingDispatcher> => {
      const { data } = await sdkRemoveDispatcher({
        path: { dispatcher_id: id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useLearnNow() {
  const refresh = useRefreshRegister();
  return useMutation({
    mutationFn: async (): Promise<RoutingSettingsInfo> => {
      const { data } = await sdkLearn({ throwOnError: true });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useAutoRouteTest() {
  return useMutation({
    mutationFn: async (body: AutoRouteRequest): Promise<AutoRouteResponse> => {
      const { data } = await sdkRunAutoRoute({ body, throwOnError: true });
      return data;
    },
  });
}
